import { ErrorEvent } from '@sentry/types/types/event'

/**
 * Determines if a Sentry event is a Firebase error that should NOT be reported.
 *
 * Firebase auth errors like auth/network-request-failed, auth/wrong-password, etc.
 * are expected user-facing errors that are out of our control. These errors are
 * handled gracefully in the UX via authErrorTranslate() and should not create
 * noise in Sentry. Only unexpected Firebase errors should be reported.
 *
 * Authorized operators can inspect the exact server-side outcome emitted by
 * Firebase in Google Cloud Logging with this query:
 * `resource.type="identitytoolkit_project"`
 * Error details are recorded under `jsonPayload.status` when present.
 *
 * This function is used in Sentry's beforeSend hook to filter events before they
 * are sent to Sentry servers, preventing unnecessary noise from expected auth errors.
 *
 * Error codes filtered (from auth-error-translate.ts):
 * - auth/network-request-failed: Network connectivity issues
 * - auth/user-not-found: User doesn't exist (login flow)
 * - auth/wrong-password: Incorrect password (login flow)
 * - auth/invalid-credential: Invalid email/password with email enumeration protection enabled
 * - auth/email-already-in-use: Email already registered (signup flow)
 * - auth/email-already-exists: Email already registered (signup flow)
 * - auth/invalid-email: Invalid email format
 * - auth/invalid-password: Invalid password format
 * - auth/weak-password: Password doesn't meet requirements
 * - auth/too-many-requests: Rate limiting
 * - auth/user-disabled: Account disabled
 * - storage/object-not-found: File not found in Firebase Storage
 *
 * @param {ErrorEvent} event - A Sentry Event type of event ('@sentry/types/types/event')
 * @returns {boolean} - true if this error should NOT be reported to Sentry
 */
export function isNoReportFirebaseErrorEvent(event: ErrorEvent): boolean {
  // Expected Firebase error codes that should NOT be reported to Sentry
  const NO_REPORT_FIREBASE_ERROR_CODES = [
    'auth/network-request-failed',
    'auth/user-not-found',
    'auth/wrong-password',
    'auth/invalid-credential',
    'auth/email-already-in-use',
    'auth/email-already-exists',
    'auth/invalid-email',
    'auth/invalid-password',
    'auth/weak-password',
    'auth/too-many-requests',
    'auth/user-disabled',
    'storage/object-not-found',
  ]

  // Get the first exception from the event
  const exception = event.exception?.values?.[0]
  if (!exception) return false

  // Check if this is a FirebaseError by examining the exception type
  if (exception.type !== 'FirebaseError') return false

  // Check if the error message contains any of the expected error codes
  const errorMessage = exception.value || ''
  const isExpectedError = NO_REPORT_FIREBASE_ERROR_CODES.some((code) => errorMessage.includes(code))

  return isExpectedError
}
